import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, Briefcase, Users, Calendar, Star, MapPin, ChevronDown, ArrowRight, CheckCircle, Sparkles, Loader2, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { SEO, organizationSchema, websiteSchema } from '../components/SEO';
import { REGIONS, LANGUAGES, SKILLS, INDUSTRIES, getLanguageForCountry, getMainCategories, getSubCategories, getSubSubCategories, getTrainingTypes } from '../data/globalData';
import { TrainerAdRunner, CompanyAdRunner } from '../components/AdRunner';

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [isAISearch, setIsAISearch] = useState(true);
  const [aiSearching, setAiSearching] = useState(false);
  const [searchType, setSearchType] = useState('trainers');
  const [selectedCountry, setSelectedCountry] = useState('');
  
  // Premium trainers and companies for ad runners
  const [premiumTrainers, setPremiumTrainers] = useState([]);
  const [premiumCompanies, setPremiumCompanies] = useState([]);
  const [platformStats, setPlatformStats] = useState(null);
  
  // 3-tier training category state
  const [mainCategory, setMainCategory] = useState('');
  const [subCategory, setSubCategory] = useState('');
  const [subSubCategory, setSubSubCategory] = useState('');
  
  // Fetch premium trainers and companies for ad runners
  useEffect(() => {
    const fetchPremiumData = async () => {
      try {
        const [trainersRes, companiesRes] = await Promise.all([
          axios.get(`${API}/trainers/premium/featured`),
          axios.get(`${API}/corporates/premium/featured`)
        ]);
        setPremiumTrainers(trainersRes.data);
        setPremiumCompanies(companiesRes.data);
      } catch (error) {
        console.error('Error fetching premium data:', error);
      }
    };
    fetchPremiumData();
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/stats/platform`);
        setPlatformStats(res.data);
      } catch (e) { /* silent */ }
    };
    fetchStats();
  }, []);
  
  const [filters, setFilters] = useState({
    skill: '',
    location: '',
    industry: '',
    training_type: '',
    experience_min: '',
    experience_max: '',
    rate_min: '',
    rate_max: '',
    rating_min: '',
    language: 'English',
    availability: ''
  });

  // Get cities for selected country
  const getCitiesForCountry = (country) => {
    if (!country) return [];
    for (const [region, countries] of Object.entries(REGIONS)) {
      if (countries[country]) {
        return countries[country];
      }
    }
    return [];
  };

  // Get all countries grouped by region
  const getCountriesByRegion = () => {
    const result = {};
    Object.entries(REGIONS).forEach(([region, countries]) => {
      result[region] = Object.keys(countries);
    });
    return result;
  };

  // Handle country change - auto-select language
  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    setFilters(prev => ({
      ...prev,
      location: '',
      language: getLanguageForCountry(country)
    }));
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() && !showFilters) {
      toast.error('Please enter a search query');
      return;
    }

    // If AI search is enabled and query looks like natural language
    if (isAISearch && searchQuery.trim() && searchQuery.split(' ').length > 2) {
      setAiSearching(true);
      try {
        const response = await axios.post(`${API}/search/natural`, {
          query: searchQuery,
          search_type: searchType
        });
        
        // Navigate to search results with the parsed filters
        const params = new URLSearchParams();
        params.set('type', searchType);
        params.set('nl_query', searchQuery);
        
        const parsedFilters = response.data.parsed_filters;
        Object.entries(parsedFilters).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            if (Array.isArray(value)) {
              params.set(key, value.join(','));
            } else {
              params.set(key, String(value));
            }
          }
        });
        
        toast.success(`Found ${response.data.results_count} results!`);
        navigate(`/search?${params.toString()}`, { 
          state: { 
            nlResults: response.data.results,
            parsedFilters: response.data.parsed_filters,
            originalQuery: searchQuery
          }
        });
      } catch (error) {
        console.error('AI search failed:', error);
        // Fallback to regular search
        handleRegularSearch();
      } finally {
        setAiSearching(false);
      }
    } else {
      handleRegularSearch();
    }
  };

  const handleRegularSearch = () => {
    const params = new URLSearchParams();
    params.set('type', searchType);
    if (searchQuery) params.set('query', searchQuery);
    Object.entries(filters).forEach(([key, value]) => {
      if (value && (Array.isArray(value) ? value.length > 0 : true)) {
        params.set(key, Array.isArray(value) ? value.join(',') : value);
      }
    });
    navigate(`/search?${params.toString()}`);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <SEO
        title={null}
        description="India's leading corporate training marketplace. Connect with 500+ expert trainers across Leadership, Technology, Sales, Compliance & more. Find trainers or post training requirements globally."
        path="/"
        jsonLd={[organizationSchema, websiteSchema]}
      />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md z-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <span className="font-outfit font-bold text-xl text-slate-900">Training Solutions</span>
            </Link>
            
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/search?type=trainers" className="text-slate-600 hover:text-slate-900 font-medium transition-colors" data-testid="nav-find-trainers">
                Find Trainers
              </Link>
              <Link to="/search?type=jobs" className="text-slate-600 hover:text-slate-900 font-medium transition-colors" data-testid="nav-browse-jobs">
                Browse Requirements
              </Link>
              <Link to="/pricing" className="text-slate-600 hover:text-slate-900 font-medium transition-colors" data-testid="nav-pricing">
                Pricing
              </Link>
              <Link to="/about" className="text-slate-600 hover:text-slate-900 font-medium transition-colors" data-testid="nav-about">
                About
              </Link>
            </nav>

            <div className="flex items-center gap-3">
              {user ? (
                <Button 
                  onClick={() => navigate(user.user_type === 'trainer' ? '/trainer/dashboard' : '/corporate/dashboard')}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6"
                  data-testid="dashboard-btn"
                >
                  Dashboard
                </Button>
              ) : (
                <>
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate('/login')}
                    className="text-slate-600 hover:text-slate-900"
                    data-testid="login-btn"
                  >
                    Sign In
                  </Button>
                  <Button 
                    onClick={() => navigate('/register')}
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6"
                    data-testid="register-btn"
                  >
                    Get Started
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section - Google Style */}
      <main className="pt-16">
        <div className="min-h-[90vh] flex flex-col items-center justify-center px-4">
          {/* Logo & Tagline */}
          <div className="text-center mb-12 animate-fadeIn">
            <h1 className="font-outfit text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 tracking-tight mb-6">
              Training Solutions
            </h1>
            <p className="text-lg md:text-xl text-slate-500 max-w-2xl mx-auto">
              Connect with expert corporate trainers or find your next training opportunity
            </p>
          </div>

          {/* Search Bar */}
          <div className="w-full max-w-3xl mx-auto animate-fadeIn stagger-1" data-testid="search-container">
            {/* Search Type Toggle */}
            <div className="flex justify-center gap-2 mb-4">
              <button
                onClick={() => setSearchType('trainers')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  searchType === 'trainers'
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid="search-type-trainers"
              >
                <Users className="w-4 h-4 inline mr-2" />
                Find Trainers
              </button>
              <button
                onClick={() => setSearchType('jobs')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  searchType === 'jobs'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid="search-type-jobs"
              >
                <Briefcase className="w-4 h-4 inline mr-2" />
                Find Requirements
              </button>
            </div>

            {/* AI Search Toggle */}
            <div className="flex items-center justify-center gap-2 mb-4">
              <button
                onClick={() => setIsAISearch(!isAISearch)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  isAISearch
                    ? 'bg-purple-100 text-purple-700 border border-purple-200'
                    : 'bg-slate-100 text-slate-500'
                }`}
                data-testid="ai-search-toggle"
              >
                <Sparkles className={`w-3 h-3 ${isAISearch ? 'text-purple-600' : ''}`} />
                AI-Powered Search {isAISearch ? 'ON' : 'OFF'}
              </button>
            </div>

            <div className="relative">
              {isAISearch ? (
                <Sparkles className="absolute left-5 top-1/2 transform -translate-y-1/2 text-purple-500 w-5 h-5" />
              ) : (
                <Search className="absolute left-5 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              )}
              <Input
                type="text"
                placeholder={isAISearch 
                  ? (searchType === 'trainers' 
                    ? "Try: 'Looking for a leadership trainer in Delhi with 10 years experience...'" 
                    : "Try: 'Leadership training in Mumbai, budget above 50000...'")
                  : "Search for trainers, skills, or training topics..."
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                className={`w-full h-14 pl-14 pr-4 rounded-full border shadow-lg focus:ring-2 focus:border-transparent text-lg transition-all hover:shadow-xl ${
                  isAISearch 
                    ? 'border-purple-200 focus:ring-purple-500 bg-purple-50/30' 
                    : 'border-slate-200 focus:ring-blue-500'
                }`}
                data-testid="search-input"
              />
              {aiSearching && (
                <div className="absolute right-5 top-1/2 transform -translate-y-1/2">
                  <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
                </div>
              )}
            </div>

            {isAISearch && (
              <p className="text-center text-xs text-purple-600 mt-2">
                Type naturally - our AI will understand your requirements
              </p>
            )}

            {/* Filter Toggle */}
            <div className="flex justify-center mt-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 text-slate-500 hover:text-slate-700 font-medium text-sm transition-colors"
                data-testid="toggle-filters-btn"
              >
                <span>Advanced Filters (10 criteria)</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </button>
            </div>

            {/* Expanded Filters */}
            {showFilters && (
              <div className="mt-6 p-6 bg-white border border-slate-200 rounded-2xl shadow-lg animate-fadeIn" data-testid="filters-panel">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Skills Dropdown */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Skill</label>
                    <select
                      value={filters.skill}
                      onChange={(e) => setFilters(prev => ({ ...prev, skill: e.target.value }))}
                      className="w-full h-10 px-3 rounded-lg border border-slate-200 text-slate-700 focus:ring-2 focus:ring-blue-500"
                      data-testid="filter-skill"
                    >
                      <option value="">Any Skill</option>
                      {SKILLS.map(skill => (
                        <option key={skill} value={skill}>{skill}</option>
                      ))}
                    </select>
                  </div>

                  {/* Country */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Country</label>
                    <select
                      value={selectedCountry}
                      onChange={(e) => handleCountryChange(e.target.value)}
                      className="w-full h-10 px-3 rounded-lg border border-slate-200 text-slate-700 focus:ring-2 focus:ring-blue-500"
                      data-testid="filter-country"
                    >
                      <option value="">Any Country</option>
                      {Object.entries(getCountriesByRegion()).map(([region, countries]) => (
                        <optgroup key={region} label={`── ${region} ──`}>
                          {countries.map(country => (
                            <option key={country} value={country}>{country}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>

                  {/* City */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">City</label>
                    <select
                      value={filters.location}
                      onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
                      className="w-full h-10 px-3 rounded-lg border border-slate-200 text-slate-700 focus:ring-2 focus:ring-blue-500"
                      data-testid="filter-location"
                      disabled={!selectedCountry}
                    >
                      <option value="">{selectedCountry ? 'Select City' : 'Select Country First'}</option>
                      {getCitiesForCountry(selectedCountry).map(city => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>

                  {/* Industry */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Industry</label>
                    <select
                      value={filters.industry}
                      onChange={(e) => setFilters(prev => ({ ...prev, industry: e.target.value }))}
                      className="w-full h-10 px-3 rounded-lg border border-slate-200 text-slate-700 focus:ring-2 focus:ring-blue-500"
                      data-testid="filter-industry"
                    >
                      <option value="">Any Industry</option>
                      {INDUSTRIES.map(ind => (
                        <option key={ind} value={ind}>{ind}</option>
                      ))}
                    </select>
                  </div>

                  {/* 3-Tier Training Category Selection */}
                  <div className="col-span-full">
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Training Category</label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      {/* Main Category */}
                      <select
                        value={mainCategory}
                        onChange={(e) => {
                          setMainCategory(e.target.value);
                          setSubCategory('');
                          setSubSubCategory('');
                          setFilters(prev => ({ ...prev, training_type: '' }));
                        }}
                        className="w-full h-10 px-3 rounded-lg border border-slate-200 text-slate-700 focus:ring-2 focus:ring-blue-500 text-sm"
                        data-testid="filter-main-category"
                      >
                        <option value="">Select Main Category</option>
                        {getMainCategories().map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>

                      {/* Sub Category */}
                      <select
                        value={subCategory}
                        onChange={(e) => {
                          setSubCategory(e.target.value);
                          setSubSubCategory('');
                          setFilters(prev => ({ ...prev, training_type: '' }));
                        }}
                        className="w-full h-10 px-3 rounded-lg border border-slate-200 text-slate-700 focus:ring-2 focus:ring-blue-500 text-sm"
                        data-testid="filter-sub-category"
                        disabled={!mainCategory}
                      >
                        <option value="">{mainCategory ? 'Select Sub Category' : 'Select Main First'}</option>
                        {getSubCategories(mainCategory).map(sub => (
                          <option key={sub} value={sub}>{sub}</option>
                        ))}
                      </select>

                      {/* Sub-Sub Category */}
                      <select
                        value={subSubCategory}
                        onChange={(e) => {
                          setSubSubCategory(e.target.value);
                          setFilters(prev => ({ ...prev, training_type: '' }));
                        }}
                        className="w-full h-10 px-3 rounded-lg border border-slate-200 text-slate-700 focus:ring-2 focus:ring-blue-500 text-sm"
                        data-testid="filter-subsub-category"
                        disabled={!subCategory}
                      >
                        <option value="">{subCategory ? 'Select Training Area' : 'Select Sub First'}</option>
                        {getSubSubCategories(mainCategory, subCategory).map(subSub => (
                          <option key={subSub} value={subSub}>{subSub}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Training Type (Final Selection) */}
                  <div className="col-span-full">
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Specific Training</label>
                    <select
                      value={filters.training_type}
                      onChange={(e) => setFilters(prev => ({ ...prev, training_type: e.target.value }))}
                      className="w-full h-10 px-3 rounded-lg border border-slate-200 text-slate-700 focus:ring-2 focus:ring-blue-500"
                      data-testid="filter-training-type"
                      disabled={!subSubCategory}
                    >
                      <option value="">{subSubCategory ? 'Select Specific Training' : 'Select Training Area First'}</option>
                      {getTrainingTypes(mainCategory, subCategory, subSubCategory).map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  {/* Experience Range */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Experience (Years)</label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder="Min"
                        value={filters.experience_min}
                        onChange={(e) => setFilters(prev => ({ ...prev, experience_min: e.target.value }))}
                        className="w-full h-10"
                        data-testid="filter-exp-min"
                      />
                      <Input
                        type="number"
                        placeholder="Max"
                        value={filters.experience_max}
                        onChange={(e) => setFilters(prev => ({ ...prev, experience_max: e.target.value }))}
                        className="w-full h-10"
                        data-testid="filter-exp-max"
                      />
                    </div>
                  </div>

                  {/* Rate Range */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Hourly Rate (USD)</label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder="Min"
                        value={filters.rate_min}
                        onChange={(e) => setFilters(prev => ({ ...prev, rate_min: e.target.value }))}
                        className="w-full h-10"
                        data-testid="filter-rate-min"
                      />
                      <Input
                        type="number"
                        placeholder="Max"
                        value={filters.rate_max}
                        onChange={(e) => setFilters(prev => ({ ...prev, rate_max: e.target.value }))}
                        className="w-full h-10"
                        data-testid="filter-rate-max"
                      />
                    </div>
                  </div>

                  {/* Minimum Rating */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Minimum Rating</label>
                    <select
                      value={filters.rating_min}
                      onChange={(e) => setFilters(prev => ({ ...prev, rating_min: e.target.value }))}
                      className="w-full h-10 px-3 rounded-lg border border-slate-200 text-slate-700 focus:ring-2 focus:ring-blue-500"
                      data-testid="filter-rating"
                    >
                      <option value="">Any Rating</option>
                      <option value="4">4+ Stars</option>
                      <option value="4.5">4.5+ Stars</option>
                    </select>
                  </div>

                  {/* Language */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Language</label>
                    <select
                      value={filters.language}
                      onChange={(e) => setFilters(prev => ({ ...prev, language: e.target.value }))}
                      className="w-full h-10 px-3 rounded-lg border border-slate-200 text-slate-700 focus:ring-2 focus:ring-blue-500"
                      data-testid="filter-language"
                    >
                      <option value="">Any Language</option>
                      {LANGUAGES.map(lang => (
                        <option key={lang} value={lang}>{lang}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Search Button */}
                <div className="flex justify-center mt-6">
                  <Button 
                    onClick={handleSearch}
                    disabled={aiSearching}
                    className={`rounded-full px-12 h-12 text-lg font-medium ${
                      searchType === 'trainers' 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'bg-emerald-600 hover:bg-emerald-700'
                    } text-white`}
                    data-testid="search-btn"
                  >
                    {aiSearching ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Searching...
                      </>
                    ) : (
                      `Search ${searchType === 'trainers' ? 'Trainers' : 'Requirements'}`
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Quick Search Buttons */}
            {!showFilters && (
              <div className="flex flex-wrap justify-center gap-3 mt-6 animate-fadeIn stagger-2">
                <Button 
                  onClick={handleSearch}
                  disabled={aiSearching}
                  className={`rounded-full px-8 h-11 text-white ${
                    isAISearch 
                      ? 'bg-purple-600 hover:bg-purple-700' 
                      : searchType === 'trainers'
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'bg-emerald-600 hover:bg-emerald-700'
                  }`}
                  data-testid="quick-search-btn"
                >
                  {aiSearching ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      AI Searching...
                    </>
                  ) : (
                    <>
                      {isAISearch && <Sparkles className="w-4 h-4 mr-2" />}
                      Search {searchType === 'trainers' ? 'Trainers' : 'Requirements'}
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Premium Trainers Ad Runner */}
        {premiumTrainers.length > 0 && (
          <TrainerAdRunner trainers={premiumTrainers} />
        )}

        {/* Premium Companies Ad Runner */}
        {premiumCompanies.length > 0 && (
          <CompanyAdRunner companies={premiumCompanies} />
        )}

        {/* Free Launch Banner */}
        <section className="py-12 bg-emerald-600">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <div className="flex items-center justify-center gap-3 mb-3">
              <Shield className="w-6 h-6 text-emerald-200" />
              <h2 className="font-outfit text-2xl md:text-3xl font-bold text-white">Free for 6 Months!</h2>
            </div>
            <p className="text-emerald-100 text-base md:text-lg max-w-2xl mx-auto">
              All features are completely free during our launch period. Sign up now and get full access to AI-powered matching, unlimited applications, and more.
            </p>
            <Button
              onClick={() => navigate('/register')}
              className="mt-6 bg-white text-emerald-700 hover:bg-emerald-50 rounded-full px-8 h-12 font-medium"
              data-testid="free-cta-btn"
            >
              Get Started Free <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-24 bg-slate-50/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-outfit text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                How It Works
              </h2>
              <p className="text-lg text-slate-500 max-w-2xl mx-auto">
                The easiest way to connect trainers with corporate training opportunities
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12">
              {/* For Trainers */}
              <div className="bg-white rounded-2xl border border-slate-100 p-8 card-hover">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-outfit text-2xl font-semibold text-slate-900 mb-4">For Trainers</h3>
                <ul className="space-y-4">
                  {[
                    'Create your professional profile',
                    'Set your availability & hourly rate',
                    'Get discovered by top companies',
                    'Manage bookings with calendar'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-600">{item}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={() => navigate('/register?type=trainer')}
                  className="mt-8 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6"
                  data-testid="join-trainer-btn"
                >
                  Join as Trainer <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>

              {/* For Corporates */}
              <div className="bg-white rounded-2xl border border-slate-100 p-8 card-hover">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-6">
                  <Briefcase className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="font-outfit text-2xl font-semibold text-slate-900 mb-4">For Companies</h3>
                <ul className="space-y-4">
                  {[
                    'Post training requirements',
                    'Smart matching with qualified trainers',
                    'Review profiles & ratings',
                    'Book trainers instantly'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-600">{item}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={() => navigate('/register?type=corporate')}
                  className="mt-8 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-6"
                  data-testid="join-corporate-btn"
                >
                  Join as Company <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { label: 'Expert Trainers', value: platformStats ? `${platformStats.trainers}+` : '30+' },
                { label: 'Companies', value: platformStats ? `${platformStats.companies}+` : '25+' },
                { label: 'Requirements Posted', value: platformStats ? `${platformStats.requirements_posted}+` : '5+' },
                { label: 'Trainings Completed', value: platformStats ? `${platformStats.trainings_completed}+` : '0+' }
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="font-outfit text-4xl md:text-5xl font-bold text-blue-600 mb-2">{stat.value}</div>
                  <div className="text-slate-500 font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-900 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-white" />
                  </div>
                  <span className="font-outfit font-bold text-lg">Training Solutions</span>
                </div>
                <p className="text-sm text-slate-400 leading-relaxed">India's leading corporate training marketplace. Connecting expert trainers with companies worldwide.</p>
              </div>
              <div>
                <h4 className="font-semibold mb-3 text-sm">Platform</h4>
                <div className="space-y-2 text-sm text-slate-400">
                  <Link to="/search?type=trainers" className="block hover:text-white transition-colors">Find Trainers</Link>
                  <Link to="/search?type=jobs" className="block hover:text-white transition-colors">Browse Requirements</Link>
                  <Link to="/pricing" className="block hover:text-white transition-colors">Pricing</Link>
                </div>
              </div>
              <div>
                <h4 className="font-semibold mb-3 text-sm">Company</h4>
                <div className="space-y-2 text-sm text-slate-400">
                  <Link to="/about" className="block hover:text-white transition-colors">About Us</Link>
                  <Link to="/contact" className="block hover:text-white transition-colors">Contact</Link>
                </div>
              </div>
              <div>
                <h4 className="font-semibold mb-3 text-sm">Legal</h4>
                <div className="space-y-2 text-sm text-slate-400">
                  <Link to="/privacy" className="block hover:text-white transition-colors">Privacy Policy</Link>
                  <Link to="/terms" className="block hover:text-white transition-colors">Terms of Service</Link>
                </div>
              </div>
            </div>
            <div className="border-t border-slate-800 pt-8 text-center text-sm text-slate-400">
              © {new Date().getFullYear()} Training Solutions. All rights reserved.
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}
